import type { ReactNode } from "react";

export interface FooterProps {
  /**
   * Footer content — typically a slim status / version / copyright
   * strip. The v35 prototype has NO footer (its full-page shell is
   * header + body only); this primitive exists so surfaces that DO want
   * a footer get a consistent, tokenized strip rather than re-inventing
   * one. Web composes a minimal version strip into this slot.
   */
  children: ReactNode;
  /** Optional extra classes for the strip. */
  className?: string;
}

/**
 * Footer — surface-agnostic bottom strip.
 *
 * Mirrors the Header chrome (surface bg, 1px top border, never shrinks)
 * so a shell that opts into a footer reads as a balanced frame. Renders
 * a `<footer>` landmark. Content is composed by the surface.
 *
 * Tokenized: `bg-surface` / `border-border` / `text-caption` (11px
 * role) — no arbitrary values.
 */
export function Footer({ children, className }: FooterProps) {
  const strip =
    "flex items-center justify-between gap-4 px-7 py-2.5 bg-surface border-t border-border text-caption text-text-secondary shrink-0";

  return (
    <footer
      data-testid="app-footer"
      className={[strip, className].filter(Boolean).join(" ")}
    >
      {children}
    </footer>
  );
}
